import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface Company {
  id: string;
  name: string;
  slug: string | null;
  owner_id: string;
  size: string | null;
  industry: string | null;
  description: string | null;
  created_at: string;
  updated_at: string;
  trial_ends_at: string | null;
  plan: string;
  status: string;
}

export interface CompanyProfile {
  id: string;
  company_id: string;
  registry_code: string | null;
  vat_number: string | null;
  country: string | null;
  address: string | null;
  billing_email: string | null;
  created_at: string;
  updated_at: string;
}

interface CompanyData {
  company: Company | null;
  companyProfile: CompanyProfile | null;
  isOwner: boolean;
}

async function fetchCompanyData(userId: string): Promise<CompanyData> {
  const { data: profile } = await supabase
    .from("profiles")
    .select("company_id")
    .eq("user_id", userId)
    .maybeSingle();

  if (!profile?.company_id) {
    return { company: null, companyProfile: null, isOwner: false };
  }

  const { data: companyData } = await supabase
    .from("companies")
    .select("*")
    .eq("id", profile.company_id)
    .maybeSingle();

  if (!companyData) {
    return { company: null, companyProfile: null, isOwner: false };
  }

  const { data: profileData } = await supabase
    .from("company_profiles")
    .select("*")
    .eq("company_id", companyData.id)
    .maybeSingle();

  return {
    company: companyData as Company,
    companyProfile: (profileData as CompanyProfile) || null,
    isOwner: companyData.owner_id === userId,
  };
}

export function useCompany() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["company", userId],
    queryFn: () => fetchCompanyData(userId!),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });

  const company = data?.company ?? null;
  const companyProfile = data?.companyProfile ?? null;
  const isOwner = data?.isOwner ?? false;

  const createCompany = async (input: {
    name: string;
    size: string;
    industry: string;
    description?: string;
  }) => {
    if (!userId) throw new Error("Not authenticated");

    const { data: newCompany, error: companyError } = await supabase
      .from("companies")
      .insert({
        name: input.name,
        owner_id: userId,
        size: input.size,
        industry: input.industry,
        description: input.description || null,
      })
      .select()
      .single();

    if (companyError) throw companyError;

    const { error: profileError } = await supabase
      .from("profiles")
      .update({ company_id: newCompany.id })
      .eq("user_id", userId);

    if (profileError) {
      console.error("Error updating profile with company_id:", profileError);
      throw profileError;
    }

    const { data: existingRole } = await supabase
      .from("user_roles")
      .select("id, role")
      .eq("user_id", userId)
      .maybeSingle();

    if (existingRole) {
      if (existingRole.role !== "admin") {
        const { error: roleUpdateError } = await supabase
          .from("user_roles")
          .update({ role: "admin" })
          .eq("id", existingRole.id);
        if (roleUpdateError) console.error("Error updating user role:", roleUpdateError);
      }
    } else {
      const { error: roleInsertError } = await supabase
        .from("user_roles")
        .insert({ user_id: userId, role: "admin" });
      if (roleInsertError) console.error("Error inserting user role:", roleInsertError);
    }

    const { error: membershipError } = await supabase
      .from("company_memberships")
      .upsert({
        user_id: userId,
        company_id: newCompany.id,
        role: "admin",
        status: "active",
        joined_at: new Date().toISOString(),
      }, { onConflict: "user_id,company_id" });

    if (membershipError) {
      console.error("Error creating company membership:", membershipError);
    }

    localStorage.setItem(`active_company_${userId}`, newCompany.id);

    const { error: companyProfileError } = await supabase
      .from("company_profiles")
      .insert({ company_id: newCompany.id });

    if (companyProfileError) {
      console.error("Error creating company profile:", companyProfileError);
    }

    // Update cache immediately
    queryClient.setQueryData(["company", userId], {
      company: newCompany as Company,
      companyProfile: {
        id: "",
        company_id: newCompany.id,
        registry_code: null,
        vat_number: null,
        country: null,
        address: null,
        billing_email: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      } as CompanyProfile,
      isOwner: true,
    });

    return newCompany;
  };

  const updateCompany = async (updates: Partial<Company>) => {
    if (!company) throw new Error("No company");

    const { error } = await supabase
      .from("companies")
      .update(updates)
      .eq("id", company.id);

    if (error) throw error;

    queryClient.setQueryData(["company", userId], (old: CompanyData | undefined) => {
      if (!old) return old;
      return { ...old, company: { ...old.company!, ...updates } };
    });
  };

  const updateCompanyProfile = async (updates: Partial<CompanyProfile>) => {
    if (!company) throw new Error("No company");

    if (companyProfile) {
      const { error } = await supabase
        .from("company_profiles")
        .update(updates)
        .eq("company_id", company.id);

      if (error) throw error;
      queryClient.setQueryData(["company", userId], (old: CompanyData | undefined) => {
        if (!old) return old;
        return { ...old, companyProfile: { ...old.companyProfile!, ...updates } };
      });
    } else {
      const { data: newProfile, error } = await supabase
        .from("company_profiles")
        .insert({ ...updates, company_id: company.id })
        .select()
        .single();

      if (error) throw error;
      queryClient.setQueryData(["company", userId], (old: CompanyData | undefined) => {
        if (!old) return old;
        return { ...old, companyProfile: newProfile as CompanyProfile };
      });
    }
  };

  const deleteCompany = async () => {
    if (!company || !isOwner) throw new Error("Not authorized");

    const { error } = await supabase
      .from("companies")
      .delete()
      .eq("id", company.id);

    if (error) throw error;

    queryClient.setQueryData(["company", userId], {
      company: null,
      companyProfile: null,
      isOwner: false,
    });
  };

  return {
    company,
    companyProfile,
    loading: isLoading,
    isOwner,
    createCompany,
    updateCompany,
    updateCompanyProfile,
    deleteCompany,
    refetch,
  };
}
